import React, { useEffect, useRef, useState } from "react";
import { Topbar, Alert } from "@components";
import { useNavigate } from "react-router-dom";
import {
	RootContainer,
	Avatar,
	Input,
	FileInput,
	ProfileInputContainer,
	SaveButton,
	AvatarWrapper,
	Form,
	NoImageIcon,
	CameraIcon,
	LevelBadge
} from "./EditProfile.style";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { PUBLIC_ROUTE } from "@router/route.constants";
import { useDispatch, useSelector } from "react-redux";
import { api } from "@api";
import { saveUser } from "@redux/auth/authSlice";
import { openAlert } from "@redux/app/appSlice";
import { localStorage } from "@utils";
import { useTranslation } from "react-i18next";
import { StarIcon, LockIcon } from '@assets';

const EditProfile = () => {
	const { t } = useTranslation();
	const [image, setImage] = useState(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [imageLoading, setImageLoading] = useState(false);
	const [data, setData] = useState({
		name: "",
		phone: "",
		email: "",
	});
	const { user } = useSelector((state) => state.auth);
	const [dynamicHeight, setDynamicHeight] = useState(0);
	const ref = useRef();

	useEffect(() => {
		setDynamicHeight(ref?.current?.offsetHeight);
	}, [ref]);

	useEffect(() => {
		setData({
			name: user.name,
			phone: user.phone,
			email: user.email,
		});
	}, [user]);

	const goBack = () => {
		navigate(`/${PUBLIC_ROUTE.HOME}/${PUBLIC_ROUTE.PROFILE}`);
	};

	const handleUpdate = () => {
		setLoading(true);
		api
			.updateProfile({
				name: data.name,
			})
			.then(({ user }) => {
				setLoading(false);
				showAlert({
					message: t("common.updateSuccessMessage"),
					successCallback: goBack,
				});
				// save token and user to redux
				dispatch(saveUser(user));

				// save token and user to localstorage
				localStorage.setUser(user);
			})
			.catch((errorMsg) => {
				setLoading(false);
				showAlert({
					message: errorMsg,
					isError: true,
				});
			});
	};

	const handleImageUpload = (event) => {
		setImageLoading(true);
		const file = event.target.files[0];
		const reader = new FileReader();

		reader.onload = () => {
			setImage(reader.result);
		};

		reader.readAsDataURL(file);
		api
			.getPresignedUrl({
				upload_type: "profile-images",
				content_type: file.type,
			})
			.then(({ presigned_url }) => {
				api
					.uploadMedia({
						url: presigned_url.url,
						fields: presigned_url.fields,
						file,
					})
					.then(() => {
						api
							.updateProfile({
								name: user.name,
								image: `${presigned_url.url}${presigned_url.fields.Key}`,
							})
							.then(({ user }) => {
								setImageLoading(false);
								showAlert({
									message: t("common.updateSuccessMessage"),
								});
								// save token and user to redux
								dispatch(saveUser(user));

								// save token and user to localstorage
								localStorage.setUser(user);
							})
							.catch((errorMsg) => {
								handleImageUploadFailed(errorMsg);
							});
					})
					.catch((errorMsg) => {
						handleImageUploadFailed(errorMsg);
					});
			})
			.catch((errorMsg) => {
				handleImageUploadFailed(errorMsg);
			});
	};

	const handleImageUploadFailed = (message) => {
		setImage(null);
		setImageLoading(false);
		showAlert({
			message,
			isError: true,
		});
	};

	const showAlert = (data) => {
		dispatch(openAlert(data));
	};

	let profileSrc = null;
	if (image) {
		profileSrc = image;
	} else if (user.image) {
		profileSrc = user.image;
	}

	return (
		<>
			<Topbar
				title={t("profile.profileEditTitle")}
				goBack={goBack}
				shadow={false}
			/>
			<RootContainer style={{ position: "relative" }}>
				<div style={{ position: "relative", marginTop: 28 }}>
					{profileSrc ? (
						<AvatarWrapper>
							<Avatar size={120} src={profileSrc} />
						</AvatarWrapper>
					) : (
						<NoImageIcon
							style={{
								width: "120px",
								height: "120px",
							}}
						/>
					)}
					<ProfileInputContainer isImg={profileSrc ? true : false}>
						{imageLoading ? (
							<Spin indicator={<LoadingOutlined spin />} />
						) : (
							<>
								<FileInput
									id="file-upload"
									type="file"
									onChange={handleImageUpload}
									accept="image/*"
								/>
								<label htmlFor="file-upload">
									<CameraIcon />
								</label>
							</>
						)}
					</ProfileInputContainer>
				</div>

				<LevelBadge>
					<StarIcon /> {`Level ${user.level}`}
				</LevelBadge>

				{/* <PhoneWrapper>
					<Phone text={true}>{t("profile.phone")}</Phone>{" "}
					<Phone>{data.phone}</Phone>
				</PhoneWrapper> */}

				<div style={{ width: "100%" }}>
					<Form layout="vertical" dynamicHeight={dynamicHeight}>
						<Form.Item
							label={t("profile.name")}
							rules={[
								{
									required: true,
									message: "",
								},
								{
									min: 4,
									max: 45,
									message: t("auth.registerNameValidate"),
								},
							]}
						>
							<Input
								placeholder={t("profile.enterName")}
								value={data.name}
								onChange={(e) =>
									setData((prev) => ({ ...prev, name: e.target.value }))
								}
							/>
						</Form.Item>

						<Form.Item label={data.phone ? t("profile.phone") : t("profile.email")}>
							<Input
								inputType="phone"
								readOnly
								value={data.phone || data.email}
								placeholder={t("profile.enterPhone")}
								suffix={<LockIcon />}
							/>
						</Form.Item>

						<Form.Item>
							<SaveButton
								type="default"
								onClick={handleUpdate}
								loading={loading}
								disabled={data.name.length < 4 || data.name.length > 45}
								htmlType="submit"
							>
								{t("common.save")}
							</SaveButton>
						</Form.Item>
					</Form>
				</div>
			</RootContainer>

			<Alert />
		</>
	);
};

export default EditProfile;
